import React from 'react';
import PropTypes from 'prop-types';
import { Avatar } from '@ublend-npm/aulaui-next';
import { useHistory } from 'react-router-dom';

import { GradeType, ErrorType } from '$variables/types';
import { getExtensionTitle } from '$utils/extensions';
import { providers } from '$variables/constants';
import { useFlags } from 'launchdarkly-react-client-sdk';

import List, {
  EmptyListItemText,
  ListItem,
  RightDecoration,
  ListItemContent,
  ListItemPrimaryText,
  ListItemSecondaryText,
  ListItemAvatar,
  ListItemSecondaryHeaderText,
  ListItemTertiaryText,
} from '$components/List/List';
import Spinner from '$components/Spinner';
import GradeLabel from '$components/GradeLabel';
import EditGrade from '$components/EditGrade';
import ExternalLink from '$components/ExternalLink';
import Pill from '$components/Pill';
import { buildHandinSubmissionURL } from '$utils/buildHandinURLs';
import hasGrade from '$utils/hasGrade';
import {
  ListItemErrorText,
  HeaderItem,
  RightGradeDecorations,
  KeepOnOneLine,
  ColumnsOnMobile,
  StudentNameContainer,
  PillContainer,
} from './Grades.styled';

const Grades = ({
  grades,
  isLoading,
  externalId,
  spaceId,
  hasExternalLink,
  disabledText,
  disableAvatar,
  disableInfoTag,
  editable,
  onEditGrade,
  gradeType,
  assignmentId,
  headerText,
  gradesHaveBeenReleased,
  isClickable,
  saveManualGradeErrors,
  provider,
}) => {
  const history = useHistory();
  const { ssAstudentIdShowInCsVandSubList } = useFlags();

  const moveToStudentPage = (studentId) => () => {
    history.push(`/${spaceId}/students/${studentId}`);
  };

  if (isLoading) {
    return <Spinner />;
  }

  if (!grades.length) {
    return (
      <List>
        <ListItem>
          <EmptyListItemText>
            Seems like there are no students in this space.
          </EmptyListItemText>
        </ListItem>
      </List>
    );
  }

  return (
    <List testId="grades-list" headerText={headerText}>
      {editable && (
        <HeaderItem>
          <ListItemSecondaryHeaderText>Student</ListItemSecondaryHeaderText>
          <ListItemSecondaryHeaderText>Grade</ListItemSecondaryHeaderText>
        </HeaderItem>
      )}
      {grades.map(
        ({
          customStudentId,
          studentId,
          firstName,
          lastName,
          email,
          avatar,
          displayGrade: grade,
          error,
          grade: originalGrade,
          hasSubmission,
          lateSubmission,
          isInExtension,
          extension,
        }) => {
          const handleClick = isClickable ? moveToStudentPage(studentId) : null;
          const notSubmitted =
            provider !== providers.MANUAL && hasSubmission === false;
          const notGraded =
            provider !== providers.MANUAL &&
            hasSubmission &&
            !hasGrade({ displayGrade: grade });

          return (
            <ListItem key={studentId} onClick={handleClick}>
              <ColumnsOnMobile>
                <KeepOnOneLine>
                  {!disableAvatar && (
                    <ListItemAvatar>
                      <Avatar
                        size="large"
                        name={`${firstName} ${lastName}`}
                        src={avatar}
                      />
                    </ListItemAvatar>
                  )}
                  <ListItemContent>
                    <StudentNameContainer>
                      <ListItemPrimaryText>{`${firstName} ${lastName}`}</ListItemPrimaryText>
                      {!disableInfoTag && notSubmitted && (
                        <PillContainer>
                          <Pill size="compact" look="paleRed">
                            Not submitted
                          </Pill>
                        </PillContainer>
                      )}
                      {!disableInfoTag && notGraded && (
                        <PillContainer>
                          <Pill size="compact" look="lightBlue">
                            Not graded
                          </Pill>
                        </PillContainer>
                      )}
                      {!disableInfoTag && lateSubmission && (
                        <PillContainer>
                          <Pill size="compact" look="cream">
                            Late submission
                          </Pill>
                        </PillContainer>
                      )}
                    </StudentNameContainer>
                    <ListItemSecondaryText>{email}</ListItemSecondaryText>
                    {ssAstudentIdShowInCsVandSubList && customStudentId && (
                      <ListItemTertiaryText>
                        <strong>Student ID</strong> : {customStudentId}
                      </ListItemTertiaryText>
                    )}
                    {isInExtension && (
                      <ListItemTertiaryText>
                        {getExtensionTitle({ extension })}
                      </ListItemTertiaryText>
                    )}
                    {error && <ListItemErrorText>{error}</ListItemErrorText>}
                  </ListItemContent>
                </KeepOnOneLine>
                <RightGradeDecorations>
                  {hasGrade({ displayGrade: grade }) && !editable && (
                    <RightDecoration>
                      <GradeLabel grade={grade} />
                    </RightDecoration>
                  )}
                  {editable && (
                    <RightDecoration>
                      <EditGrade
                        gradeType={gradeType}
                        grade={originalGrade}
                        onSave={onEditGrade}
                        studentId={studentId}
                        assignmentId={assignmentId}
                        gradesHaveBeenReleased={gradesHaveBeenReleased}
                        error={!!saveManualGradeErrors[studentId]}
                      />
                    </RightDecoration>
                  )}
                  {hasExternalLink && (
                    <RightDecoration>
                      <ExternalLink
                        to={buildHandinSubmissionURL({
                          studentId,
                          externalId,
                          space: spaceId,
                        })}
                        disabledText={disabledText}
                      />
                    </RightDecoration>
                  )}
                </RightGradeDecorations>
              </ColumnsOnMobile>
            </ListItem>
          );
        }
      )}
    </List>
  );
};

Grades.propTypes = {
  grades: PropTypes.arrayOf(GradeType).isRequired,
  isLoading: PropTypes.bool.isRequired,
  externalId: PropTypes.string,
  assignmentId: PropTypes.string,
  spaceId: PropTypes.string,
  hasExternalLink: PropTypes.bool,
  disabledText: PropTypes.string,
  disableAvatar: PropTypes.bool,
  disableInfoTag: PropTypes.bool,
  editable: PropTypes.bool,
  onEditGrade: PropTypes.func,
  gradeType: PropTypes.string,
  headerText: PropTypes.string,
  gradesHaveBeenReleased: PropTypes.bool,
  isClickable: PropTypes.bool,
  saveManualGradeErrors: PropTypes.objectOf(ErrorType),
  provider: PropTypes.string,
};

Grades.defaultProps = {
  externalId: '',
  assignmentId: '',
  spaceId: '',
  hasExternalLink: false,
  disabledText: null,
  disableAvatar: false,
  disableInfoTag: false,
  editable: false,
  onEditGrade: null,
  gradeType: null,
  headerText: null,
  gradesHaveBeenReleased: false,
  isClickable: true,
  saveManualGradeErrors: {},
  provider: '',
};

export default Grades;
